import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from '../services/api';
import { 
  ArrowLeft, Camera, Upload, Sparkles, Loader2, X, Trash2, 
  ScanLine, ImageIcon, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const HomeworkScanner = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('check'); // 'check' = single image | 'solved' = question + answer
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [answerPreview, setAnswerPreview] = useState(null);
  const [answerFile, setAnswerFile] = useState(null);
  const [subject, setSubject] = useState('mathematics');
  const [notes, setNotes] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const fileRef = useRef();
  const cameraRef = useRef();
  const ansFileRef = useRef();
  const ansCameraRef = useRef();

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    try { const r = await apiService.homeworkHistory(); setHistory(r.data.history); }
    catch (e) {}
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please pick an image'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image too large (max 10MB)'); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setResult(null);
  };

  const handleAnswerFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please pick an image'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image too large'); return; }
    setAnswerFile(file);
    setAnswerPreview(URL.createObjectURL(file));
    setResult(null);
  };

  const scan = async () => {
    if (!imageFile) { toast.error('Please add the question photo'); return; }
    if (mode === 'solved' && !answerFile) { toast.error('Please add your solved answer photo'); return; }
    setAnalyzing(true);
    setResult(null);
    try {
      const fd = new FormData();
      if (mode === 'check') {
        fd.append('image', imageFile);
        fd.append('subject', subject);
        fd.append('notes', notes);
        const r = await apiService.scanHomework(fd);
        setResult(r.data);
      } else {
        fd.append('question_image', imageFile);
        fd.append('answer_image', answerFile);
        fd.append('subject', subject);
        fd.append('notes', notes);
        const r = await apiService.uploadSolvedHomework(fd);
        setResult(r.data);
      }
      toast.success('Analysis complete!');
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analysis failed');
    } finally { setAnalyzing(false); }
  };

  const clearAll = () => {
    setImagePreview(null); setImageFile(null);
    setAnswerPreview(null); setAnswerFile(null);
    setResult(null); setNotes('');
  };

  const deleteOne = async (id) => {
    await apiService.deleteHomework(id);
    loadHistory();
    toast.success('Removed');
  };

  const subjects = [
    { id: 'mathematics', icon: '🧮', label: 'Math' },
    { id: 'science', icon: '🔬', label: 'Science' },
    { id: 'english', icon: '📖', label: 'English' },
  ];

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="homework-scanner-page">
      <div className="bg-gradient-to-r from-cyan-700 via-blue-700 to-indigo-800 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-400/20 rounded-full blur-3xl -translate-y-1/2"></div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-white/80 hover:text-white font-semibold mb-3"><ArrowLeft className="w-5 h-5" />Dashboard</button>
          <h1 className="text-3xl sm:text-4xl font-black text-white flex items-center gap-3"><ScanLine className="w-8 h-8" />Homework Scanner</h1>
          <p className="text-white/70 text-sm sm:text-base">Take a photo of your homework. AI will check answers and explain mistakes!</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-5 gap-6">
        {/* Upload card */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl p-5 border-2 border-slate-100 shadow-md">
            <h2 className="font-black text-[#1E293B] mb-3">1. Choose Mode</h2>
            <div className="grid grid-cols-2 gap-2 mb-5">
              <button onClick={() => { setMode('check'); clearAll(); }} data-testid="mode-check"
                className={`p-3 rounded-2xl border-2 text-sm font-bold transition ${mode === 'check' ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white border-transparent shadow' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                Check Problem
              </button>
              <button onClick={() => { setMode('solved'); clearAll(); }} data-testid="mode-solved"
                className={`p-3 rounded-2xl border-2 text-sm font-bold transition ${mode === 'solved' ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white border-transparent shadow' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                Review My Solution
              </button>
            </div>
            <p className="text-xs text-[#64748B] -mt-2 mb-4">
              {mode === 'check' ? 'Photograph any question — AI will solve and explain it.' : 'Photograph the question AND your answer — AI will check if you solved it correctly.'}
            </p>

            <h2 className="font-black text-[#1E293B] mb-3">2. Choose Subject</h2>
            <div className="grid grid-cols-3 gap-2 mb-5">
              {subjects.map(s => (
                <button key={s.id} onClick={() => setSubject(s.id)} data-testid={`hs-subject-${s.id}`}
                  className={`p-3 rounded-2xl text-center border-2 transition ${subject === s.id ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white border-transparent shadow-lg' : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                  <span className="block text-2xl mb-0.5">{s.icon}</span>
                  <span className="text-xs font-bold">{s.label}</span>
                </button>
              ))}
            </div>

            <h2 className="font-black text-[#1E293B] mb-3">3. {mode === 'check' ? 'Add Photo' : 'Question Photo'}</h2>
            {imagePreview ? (
              <div className="relative mb-3">
                <img src={imagePreview} alt="homework" className="w-full rounded-2xl border-2 border-slate-200" />
                <button onClick={() => { setImagePreview(null); setImageFile(null); }} className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button onClick={() => cameraRef.current?.click()} data-testid="hs-camera-btn"
                  className="aspect-square bg-gradient-to-br from-cyan-50 to-blue-50 border-2 border-dashed border-cyan-300 rounded-2xl flex flex-col items-center justify-center gap-2 hover:border-cyan-500 transition">
                  <Camera className="w-8 h-8 text-cyan-600" />
                  <span className="text-xs font-bold text-cyan-700">Camera</span>
                </button>
                <button onClick={() => fileRef.current?.click()} data-testid="hs-upload-btn"
                  className="aspect-square bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-dashed border-indigo-300 rounded-2xl flex flex-col items-center justify-center gap-2 hover:border-indigo-500 transition">
                  <Upload className="w-8 h-8 text-indigo-600" />
                  <span className="text-xs font-bold text-indigo-700">Upload</span>
                </button>
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" />
                <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
              </div>
            )}

            {/* Answer photo (only in solved mode) */}
            {mode === 'solved' && (
              <>
                <h2 className="font-black text-[#1E293B] mb-3 mt-2">4. Your Solved Answer</h2>
                {answerPreview ? (
                  <div className="relative mb-3">
                    <img src={answerPreview} alt="answer" className="w-full rounded-2xl border-2 border-slate-200" />
                    <button onClick={() => { setAnswerPreview(null); setAnswerFile(null); }} className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <button onClick={() => ansCameraRef.current?.click()} data-testid="hs-ans-camera"
                      className="aspect-square bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-dashed border-emerald-300 rounded-2xl flex flex-col items-center justify-center gap-2">
                      <Camera className="w-8 h-8 text-emerald-600" />
                      <span className="text-xs font-bold text-emerald-700">Camera</span>
                    </button>
                    <button onClick={() => ansFileRef.current?.click()} data-testid="hs-ans-upload"
                      className="aspect-square bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-dashed border-emerald-300 rounded-2xl flex flex-col items-center justify-center gap-2">
                      <Upload className="w-8 h-8 text-emerald-600" />
                      <span className="text-xs font-bold text-emerald-700">Upload</span>
                    </button>
                    <input ref={ansCameraRef} type="file" accept="image/*" capture="environment" onChange={handleAnswerFile} className="hidden" />
                    <input ref={ansFileRef} type="file" accept="image/*" onChange={handleAnswerFile} className="hidden" />
                  </div>
                )}
              </>
            )}

            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder="Any notes? (optional)"
              className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-sm font-medium mb-3 resize-none focus:border-cyan-500 outline-none" />

            <motion.button onClick={scan} disabled={!imageFile || analyzing || (mode === 'solved' && !answerFile)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              data-testid="scan-btn"
              className="w-full py-3.5 bg-gradient-to-r from-cyan-600 to-blue-700 text-white rounded-2xl font-black flex items-center justify-center gap-2 shadow-xl disabled:opacity-50">
              {analyzing ? <><Loader2 className="w-5 h-5 animate-spin" />Analyzing...</> : <><Sparkles className="w-5 h-5" />{mode === 'solved' ? 'Review My Solution' : 'Scan & Review'}</>}
            </motion.button>
          </div>

          {/* Previous scans */}
          {history.length > 0 && (
            <div className="bg-white rounded-2xl p-4 border-2 border-slate-100 shadow-md">
              <h3 className="font-black text-[#1E293B] mb-3 text-sm">Recent Scans</h3>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {history.map(h => (
                  <div key={h.id} className="p-2 bg-slate-50 rounded-xl flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold capitalize text-[#1E293B]">{h.subject}</p>
                      <p className="text-[10px] text-[#64748B]">{new Date(h.created_at).toLocaleDateString()}</p>
                    </div>
                    <button onClick={() => deleteOne(h.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Result */}
        <div className="lg:col-span-3">
          <AnimatePresence mode="wait">
            {analyzing ? (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="bg-white rounded-2xl border-2 border-slate-100 shadow-md p-10 text-center min-h-[400px] flex flex-col items-center justify-center">
                <ScanLine className="w-16 h-16 text-cyan-500 mb-3 animate-pulse" />
                <p className="font-black text-[#1E293B] mb-1">AI is reading your homework...</p>
                <p className="text-sm text-[#64748B]">Identifying questions, checking answers, finding mistakes</p>
              </motion.div>
            ) : result ? (
              <motion.div key="result" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl border-2 border-slate-100 shadow-md p-5 sm:p-6" data-testid="scan-result">
                <div className="flex items-center gap-2 mb-3 text-cyan-700 font-bold">
                  <Sparkles className="w-5 h-5" />
                  <span>AI Review</span>
                </div>
                <div className="prose max-w-none whitespace-pre-wrap text-[#1E293B] leading-relaxed text-sm sm:text-base">{result.review}</div>
              </motion.div>
            ) : (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-10 text-center min-h-[400px] flex flex-col items-center justify-center border-2 border-dashed border-cyan-200">
                <ImageIcon className="w-20 h-20 text-cyan-300 mb-3" />
                <p className="font-black text-[#1E293B] mb-1">No scan yet</p>
                <p className="text-sm text-[#64748B] max-w-sm">Pick a subject, take a clear photo of your homework, then tap "Scan & Review". The AI will read your work and explain any mistakes!</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default HomeworkScanner;
